import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { LoadErrorState, LoadingState } from '@/components/states';
import styles from './AdminListRegion.module.css';

type ListQuery<T> = Pick<
  UseQueryResult<T>,
  'data' | 'error' | 'isError' | 'isPlaceholderData'
>;

type Props<T> = {
  readonly query: ListQuery<T>;
  readonly errorTitle: string;
  /** The list's URL carries search, filters, tabs or a cursor; see LoadErrorState. */
  readonly filterable?: boolean;
  /** Render ONE element: the region wraps it, so a fragment loses the parent Stack gap. */
  readonly children: (data: T) => ReactNode;
};

/**
 * The list area of an admin page. The page frame (shell, header, search box,
 * filters, tabs) renders around it and stays mounted, so a first load, a
 * failed search or a page turn never unmounts the search box mid-typing.
 * While a changed search or page loads, the previous rows stay on screen
 * dimmed, busy and inert: they don't read as the answer to the new query, and
 * their row actions and page links can't fire against a list that is being
 * replaced (an "Older" link would pair the new filters with the old cursor).
 */
export function AdminListRegion<T>({
  query,
  errorTitle,
  filterable = false,
  children,
}: Props<T>) {
  if (query.isError) {
    return (
      <LoadErrorState title={errorTitle} error={query.error} filterable={filterable} />
    );
  }
  if (query.data === undefined) return <LoadingState />;
  return (
    <div
      aria-busy={query.isPlaceholderData || undefined}
      inert={query.isPlaceholderData || undefined}
      className={query.isPlaceholderData ? styles.stale : styles.region}
    >
      {children(query.data)}
    </div>
  );
}
